import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { metadataAPI } from '../api/enhancedApiService';

/**
 * Custom hook to fetch metadata status with tier awareness
 * @param {string} connectionId - The connection ID to fetch status for
 * @param {Object} options - Additional options for the query
 * @returns {Object} Query result object with tier status included
 */
export const useMetadataStatus = (connectionId, options = {}) => {
  const {
    enabled = !!connectionId,
    refetchInterval = connectionId ? 60000 : false, // Poll every 60 seconds (optimized from 30s)
    includeTierStatus = true,
    ...queryOptions
  } = options;

  return useQuery({
    queryKey: ['metadata-status', connectionId, { includeTierStatus }],
    queryFn: async ({ signal }) => {
      // Fetch metadata status and tier status in parallel
      const promises = [
        metadataAPI.getMetadataStatus(connectionId, { forceFresh: true, signal })
      ];

      if (includeTierStatus) {
        promises.push(metadataAPI.getTierStatus(connectionId, { forceFresh: false, signal }));
      }

      const results = await Promise.allSettled(promises);

      const metadataResult = results[0].status === 'fulfilled' ? results[0].value : null;
      const tierResult = includeTierStatus && results[1]?.status === 'fulfilled' ? results[1].value : null;

      return {
        metadata: metadataResult,
        tierStatus: tierResult
      };
    },
    enabled: enabled,
    refetchInterval: refetchInterval,
    ...queryOptions,
    // If tasks are pending, we want to poll more frequently
    refetchIntervalInBackground: true,
    // Transform the data to handle different API response formats
    select: (data) => {
      // console.log("Metadata status received:", data);

      // Extract metadata response
      const metadataData = data?.metadata?.data || data?.metadata;

      // Extract tier status and transform to expected format
      const tierStatusData = data?.tierStatus;

      let transformedTierStatus = null;
      if (tierStatusData) {
        // Transform backend format to frontend format
        // Backend sends: tier1, tier2, tier3, tier4, tier5_lineage, tier6_usage
        // Frontend uses: tier1_discovery, tier2_schema, tier3_changes, tier4_profiling, tier5_lineage, tier6_usage
        const tierMapping = {
          tier1: 'tier1_discovery',
          tier2: 'tier2_schema',
          tier3: 'tier3_changes',
          tier4: 'tier4_profiling',
          // tier5_lineage and tier6_usage are sent with their full names
          tier5_lineage: 'tier5_lineage',
          tier6_usage: 'tier6_usage'
        };

        const tiers = {};
        Object.keys(tierMapping).forEach(backendKey => {
          const frontendKey = tierMapping[backendKey];
          if (tierStatusData[backendKey]) {
            tiers[frontendKey] = {
              status: tierStatusData[backendKey].last_job_status || 'pending',
              last_run_at: tierStatusData[backendKey].last_run_at,
              last_success_at: tierStatusData[backendKey].last_success_at,
              metrics: tierStatusData[backendKey].latest_metrics || {}
            };
          }
        });

        // Calculate last execution from most recent tier run
        const lastExecution = tierStatusData.summary?.last_collection_at ||
                             tierStatusData.tier1?.last_run_at ||
                             tierStatusData.tier2?.last_run_at ||
                             null;

        // Use coverage from backend if available, otherwise calculate from summary
        const coverage = tierStatusData.coverage || {
          total_tables: tierStatusData.summary?.tables_discovered || 0,
          tables_with_schema: tierStatusData.summary?.tables_discovered || 0,
          tables_with_profiling: 0,
          schema_coverage_percent: 0,
          profiling_coverage_percent: 0
        };

        // Check if metadata is stale (older than 24 hours)
        const isStale = lastExecution ?
          (Date.now() - new Date(lastExecution).getTime()) > 24 * 60 * 60 * 1000 :
          true;

        transformedTierStatus = {
          tiers,
          lastExecution,
          coverage,
          isStale,
          summary: tierStatusData.summary || {}
        };
      }

      return {
        // Legacy metadata status fields
        tables: metadataData?.tables || {},
        columns: metadataData?.columns || {},
        statistics: metadataData?.statistics || {},
        pending_tasks: metadataData?.pending_tasks || [],
        changes: metadataData?.changes || [],
        changes_detected: metadataData?.changes_detected || 0,

        // NEW: Tier status information
        tierStatus: transformedTierStatus
      };
    }
  });
};

/**
 * Custom hook to refresh metadata
 * @param {string} connectionId - The connection ID to refresh metadata for
 * @returns {Object} Mutation result object
 */
export const useRefreshMetadata = (connectionId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params = 'full') => {
      // Handle both string and object parameters for backwards compatibility
      if (typeof params === 'string') {
        return metadataAPI.refreshMetadata(connectionId, params);
      } else {
        // Handle object with metadata_type and priority
        const metadataType = params.metadata_type || 'full';
        const priority = params.priority || 'high';
        return metadataAPI.refreshMetadata(connectionId, metadataType, null, priority);
      }
    },
    onSuccess: () => {
      // Invalidate metadata status query to trigger a refetch
      queryClient.invalidateQueries(['metadata-status', connectionId]);

      // After a short delay, also invalidate tables and related queries
      // This gives the backend time to process the initial request
      setTimeout(() => {
        queryClient.invalidateQueries(['schema-tables', connectionId]);
        queryClient.invalidateQueries(['connection-dashboard', connectionId]);
      }, 2000);
    }
  });
};